import React from 'react';

import { Alert, Box, Button, Typography } from '@mui/material';

import { PACKAGE_SETUP_FORM_ID } from '../constants';

import PackageRoomPicker from './PackageRoomPicker';
import type { PackageRoomRow } from './PackageRoomPicker';
import PackageSetupStepComponent from './index';
import type { usePackageSetupStep } from './usePackageSetupStep';
import { getOfferPackageName, getPackagePersistId } from './utils';

interface Props {
  isStepBusy: boolean;
  onAdvanceStep: () => void;
  packageSetupStep: ReturnType<typeof usePackageSetupStep>;
  setErrorMessage: (message: string | null) => void;
}

/**
 * The Package Setup step's linking status: the "Linking..." placeholder, the matched/failed
 * result banners with a retry action, the per-package room picker, and the package
 * settings form itself. Split out of WizardStepContent since Package Setup is the one step with
 * several conditional sections instead of a single presentational component.
 */
export default function PackageSetupStepSection({
  isStepBusy,
  onAdvanceStep,
  packageSetupStep,
  setErrorMessage,
}: Props) {
  if (packageSetupStep.isLinkingPackages) {
    return <Typography>Linking rooms to packages...</Typography>;
  }

  const showLinkResult = packageSetupStep.hasAttemptedPackageLink;
  const packageRoomRows: Array<PackageRoomRow> = [
    ...packageSetupStep.sortedMatchedPackages.flatMap((pkg) => {
      const packageId = getPackagePersistId(pkg);
      return packageId
        ? [
            {
              packageId,
              packageName: getOfferPackageName(pkg),
              roomAssetName: packageSetupStep.roomAssetNameByPackageId[packageId],
              source: packageSetupStep.packageRoomSources[packageId],
            },
          ]
        : [];
    }),
    ...packageSetupStep.unmatchedPackages.flatMap(({ packageId, packageName, roomAssetName }) =>
      packageId
        ? [{ packageId, packageName, roomAssetName, source: packageSetupStep.packageRoomSources[packageId] }]
        : [],
    ),
  ];

  return (
    <>
      {showLinkResult && packageSetupStep.matchedRoomRateCount > 0 && (
        <Alert severity="success">
          Matched {packageSetupStep.matchedRoomRateCount} package
          {packageSetupStep.matchedRoomRateCount === 1 ? '' : 's'} to a room and rate.
        </Alert>
      )}

      {showLinkResult && packageSetupStep.packageLinkFailures.length > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              disabled={isStepBusy || packageSetupStep.hasUnresolvedFlexibleNightsWarning}
              size="small"
              onClick={() => void packageSetupStep.handleLinkPackages()}
            >
              Retry linking
            </Button>
          }
        >
          <Typography fontWeight={600}>
            {packageSetupStep.matchedRoomRateCount === 0
              ? 'No packages could be matched to a room and rate. Check the room chosen for each package below, then try again.'
              : `${packageSetupStep.packageLinkFailures.length} package${packageSetupStep.packageLinkFailures.length === 1 ? '' : 's'} could not be matched to a room and rate:`}
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
            {packageSetupStep.packageLinkFailures.map((failure) => (
              <li key={failure}>{failure}</li>
            ))}
          </Box>
        </Alert>
      )}

      {showLinkResult && packageSetupStep.hasUnlinkedRoomChange && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              disabled={isStepBusy || packageSetupStep.hasUnresolvedFlexibleNightsWarning}
              size="small"
              onClick={() => void packageSetupStep.handleLinkPackages()}
            >
              Re-check links
            </Button>
          }
        >
          Room changes need re-checking before you can continue.
        </Alert>
      )}

      {showLinkResult && (
        <PackageRoomPicker
          disabled={isStepBusy}
          onSelectRoomType={packageSetupStep.selectRoomTypeForPackage}
          roomTypeOptions={packageSetupStep.assignableRoomTypes}
          rows={packageRoomRows}
          selectedRoomTypeIdByPackageId={packageSetupStep.packageRoomTypeIds}
        />
      )}

      <PackageSetupStepComponent
        disabled={isStepBusy}
        flexibleNightsById={packageSetupStep.flexibleNightsById}
        flexibleNightsNextStepsCount={packageSetupStep.flexibleNightsNextStepsCount}
        flexibleNightsWithoutMaxNights={packageSetupStep.flexibleNightsWithoutMaxNights}
        formId={PACKAGE_SETUP_FORM_ID}
        onConfirmDisableFlexibleNights={packageSetupStep.confirmDisableFlexibleNights}
        onSaved={async () => {
          // Reaching here already required a clean link attempt, so nothing to relink.
          setErrorMessage(null);
          onAdvanceStep();
        }}
        onToggleFlexibleNights={packageSetupStep.toggleFlexibleNights}
        sortedPackages={packageSetupStep.sortedMatchedPackages}
      />
    </>
  );
}
